import React, { useMemo, useCallback } from 'react'
import ReactDOM from 'react-dom'
import { makeStyles } from '@material-ui/core'
import BackspaceOutlinedIcon from '@material-ui/icons/BackspaceOutlined'
import { useIntl } from 'react-intl'
import { CapeeshColors } from '../../../../../assets/ColorPalette'
import { useIsTouchDevice } from '../hooks/useIsPhone'
import { playerMessages } from '../CapeeshPlayer.i18n.Messages'

const KEYBOARD_LAYOUTS = {
    en: [
        ['q', 'w', 'e', 'r', 't', 'y', 'u', 'i', 'o', 'p'],
        ['a', 's', 'd', 'f', 'g', 'h', 'j', 'k', 'l'],
        ['z', 'x', 'c', 'v', 'b', 'n', 'm'],
    ],
    no: [
        ['q', 'w', 'e', 'r', 't', 'y', 'u', 'i', 'o', 'p', 'å'],
        ['a', 's', 'd', 'f', 'g', 'h', 'j', 'k', 'l', 'ø', 'æ'],
        ['z', 'x', 'c', 'v', 'b', 'n', 'm'],
    ],
    de: [
        ['q', 'w', 'e', 'r', 't', 'z', 'u', 'i', 'o', 'p', 'ü'],
        ['a', 's', 'd', 'f', 'g', 'h', 'j', 'k', 'l', 'ö', 'ä'],
        ['y', 'x', 'c', 'v', 'b', 'n', 'm', 'ß'],
    ],
    it: [
        ['q', 'w', 'e', 'r', 't', 'y', 'u', 'i', 'o', 'p'],
        ['a', 's', 'd', 'f', 'g', 'h', 'j', 'k', 'l'],
        ['z', 'x', 'c', 'v', 'b', 'n', 'm'],
        ['è', 'é', 'à', 'ì', 'ò', 'ù'],
    ],
    pl: [
        ['q', 'w', 'e', 'r', 't', 'y', 'u', 'i', 'o', 'p'],
        ['a', 's', 'd', 'f', 'g', 'h', 'j', 'k', 'l'],
        ['z', 'x', 'c', 'v', 'b', 'n', 'm'],
        ['ą', 'ć', 'ę', 'ł', 'ń', 'ó', 'ś', 'ź', 'ż'],
    ],
}

// Map numeric languageId values to layout keys
// These IDs come from the backend; add new mappings as needed
const LANGUAGE_ID_MAP = {
    1: 'en',    // English UK
    26: 'en',   // English US
    3: 'no',    // Norwegian
    10: 'pl',   // Polish
    4: 'de',    // German
    7: 'it',    // Italian
}

function resolveLayout(languageId) {
    if (typeof languageId === 'string') {
        const lower = languageId.toLowerCase()
        if (lower.startsWith('nb') || lower.startsWith('nn') || lower.startsWith('no')) return 'no'
        if (lower.startsWith('de')) return 'de'
        if (lower.startsWith('it')) return 'it'
        if (lower.startsWith('pl')) return 'pl'
        if (lower.startsWith('en')) return 'en'
    }
    return LANGUAGE_ID_MAP[languageId] || 'en'
}

const useStyles = makeStyles(() => ({
    container: {
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
        padding: '8px 4px',
        backgroundColor: 'rgba(0, 0, 0, 0.35)',
        borderRadius: 12,
        backdropFilter: 'blur(8px)',
        '@media (max-width: 599px)': {
            padding: '8px 4px 12px',
        },
    },
    containerDocked: {
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 10000,
        borderRadius: '12px 12px 0 0',
        backgroundColor: 'rgba(0, 0, 0, 0.85)',
        maxWidth: 680,
        margin: '0 auto',
        '@media (max-width: 599px)': {
            maxWidth: 'none',
        },
    },
    row: {
        display: 'flex',
        justifyContent: 'center',
        gap: 4,
    },
    key: {
        minWidth: 28,
        height: 40,
        padding: '0 6px',
        borderRadius: 6,
        border: '1px solid rgba(255, 255, 255, 0.2)',
        backgroundColor: 'rgba(255, 255, 255, 0.12)',
        color: '#FFFFFF',
        fontFamily: 'Rubik',
        fontSize: 16,
        fontWeight: 500,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'all 100ms ease',
        userSelect: 'none',
        WebkitTapHighlightColor: 'transparent',
        flex: '1 1 0',
        maxWidth: 36,
        '&:active': {
            transform: 'scale(0.92)',
            backgroundColor: 'rgba(255, 255, 255, 0.25)',
        },
    },
    keyDisabled: {
        opacity: 0.2,
        cursor: 'default',
        '&:active': {
            transform: 'none',
            backgroundColor: 'rgba(255, 255, 255, 0.12)',
        },
    },
    backspaceKey: {
        minWidth: 48,
        maxWidth: 56,
        flex: '1.5 1 0',
        backgroundColor: 'rgba(255, 255, 255, 0.08)',
    },
    spaceKey: {
        flex: '4 1 0',
        maxWidth: 'none',
        minWidth: 100,
        backgroundColor: 'rgba(255, 255, 255, 0.08)',
        fontSize: 13,
        color: 'rgba(255, 255, 255, 0.6)',
    },
    submitRow: {
        display: 'flex',
        justifyContent: 'center',
        gap: 6,
        paddingTop: 4,
    },
    submitButton: {
        flex: 1,
        maxWidth: 200,
        height: 40,
        borderRadius: 20,
        border: 'none',
        backgroundColor: CapeeshColors.BlueBright,
        color: '#FFFFFF',
        fontFamily: 'Rubik',
        fontSize: 15,
        fontWeight: 600,
        cursor: 'pointer',
        transition: 'all 150ms ease',
        '&:disabled': {
            opacity: 0.4,
            cursor: 'default',
        },
    },
}))

/**
 * L2OnScreenKeyboard — QWERTY keyboard for L2 writing exercises on touch devices.
 *
 * Props:
 *   languageId       – L2 language (numeric ID or string code)
 *   correctAnswers   – array of all alternative text strings (correct AND distractors for exam safety)
 *   onKeyPress(char) – called when a letter is tapped
 *   onBackspace()    – called when backspace is tapped
 *   onSubmit()       – called when check/submit is tapped
 *   inputValue       – current input value (for disabling submit when empty)
 *   submitLabel      – label for the submit button (default "Check")
 *   submitDisabled   – externally disable the submit button
 *   isExamMode       – when true, all keys are enabled (no hints)
 */
const L2OnScreenKeyboard = ({
    languageId,
    correctAnswers = [],
    onKeyPress,
    onBackspace,
    onSubmit,
    inputValue = '',
    submitLabel,
    submitDisabled = false,
    isExamMode = false,
}) => {
    const classes = useStyles()
    const intl = useIntl()
    const isTouchDevice = useIsTouchDevice()
    const resolvedSubmitLabel = submitLabel || intl.formatMessage(playerMessages.check)

    const layoutKey = resolveLayout(languageId)
    const rows = KEYBOARD_LAYOUTS[layoutKey] || KEYBOARD_LAYOUTS.en

    const enabledChars = useMemo(() => {
        if (isExamMode) return null
        const chars = new Set()
        correctAnswers.forEach((text) => {
            if (!text) return
            for (const ch of text.toLowerCase()) {
                if (ch !== ' ' && ch.trim()) chars.add(ch)
            }
        })
        chars.add(' ')
        return chars
    }, [correctAnswers, isExamMode])

    const extraChars = useMemo(() => {
        const layoutChars = new Set(rows.flat())
        const needed = new Set()
        correctAnswers.forEach((text) => {
            if (!text) return
            for (const ch of text.toLowerCase()) {
                if (ch !== ' ' && !layoutChars.has(ch) && !/\p{L}/u.test(ch)) {
                    needed.add(ch)
                }
            }
        })
        return Array.from(needed).sort()
    }, [correctAnswers, rows])

    const handleKey = useCallback(
        (char) => {
            if (!enabledChars || enabledChars.has(char)) {
                onKeyPress(char)
            }
        },
        [enabledChars, onKeyPress]
    )

    const keyboardContent = (
        <div className={`${classes.container} ${isTouchDevice ? classes.containerDocked : ''}`}>
            {rows.map((row, rowIdx) => (
                <div key={rowIdx} className={classes.row}>
                    {row.map((ch) => {
                        const isEnabled = !enabledChars || enabledChars.has(ch)
                        return (
                            <button
                                key={ch}
                                className={`${classes.key} ${!isEnabled ? classes.keyDisabled : ''}`}
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => handleKey(ch)}
                                disabled={!isEnabled}
                                type="button"
                            >
                                {ch}
                            </button>
                        )
                    })}
                    {rowIdx === rows.length - 1 && (
                        <button
                            className={`${classes.key} ${classes.backspaceKey}`}
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={onBackspace}
                            type="button"
                        >
                            <BackspaceOutlinedIcon style={{ fontSize: 20 }} />
                        </button>
                    )}
                </div>
            ))}

            {extraChars.length > 0 && (
                <div className={classes.row}>
                    {extraChars.map((ch) => {
                        const isEnabled = !enabledChars || enabledChars.has(ch)
                        return (
                            <button
                                key={ch}
                                className={`${classes.key} ${!isEnabled ? classes.keyDisabled : ''}`}
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => handleKey(ch)}
                                disabled={!isEnabled}
                                type="button"
                            >
                                {ch}
                            </button>
                        )
                    })}
                </div>
            )}

            <div className={classes.row}>
                <button
                    className={`${classes.key} ${classes.spaceKey}`}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => handleKey(' ')}
                    type="button"
                >
                    {intl.formatMessage(playerMessages.space)}
                </button>
            </div>

            <div className={classes.submitRow}>
                <button
                    className={classes.submitButton}
                    onClick={onSubmit}
                    disabled={submitDisabled || !inputValue.trim()}
                    type="button"
                >
                    {resolvedSubmitLabel}
                </button>
            </div>
        </div>
    )

    // Portal to document.body on touch devices to escape ancestor transforms
    // that break position:fixed (e.g. fadeInWrapper animation in TestGenericNode)
    if (isTouchDevice) {
        return ReactDOM.createPortal(keyboardContent, document.body)
    }

    return keyboardContent
}

export default L2OnScreenKeyboard
